import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AddRounded from '@mui/icons-material/AddRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../context/AuthContext';
import { getUsers, createUser, updateUser, deleteUser } from '../services/usersApi';

// Sections a non-admin user's access can be toggled for.
const PERMISSION_SECTIONS = [
  { key: 'fabrics', label: 'Fabrics' },
  { key: 'finishes', label: 'Finishes' },
  { key: 'certifications', label: 'Certifications' },
  { key: 'fabric_coatings', label: 'Fabric Coatings' },
  { key: 'installations', label: 'Installations' },
  { key: 'materials', label: 'Materials' },
  { key: 'features', label: 'Features' },
  { key: 'settings', label: 'Settings' },
];

// Default = full access (everything on).
const fullPermissions = () => ({
  view_price: true,
  sections: PERMISSION_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {}),
});

const emptyForm = { name: '', email: '', password: '', role: 'user', permissions: fullPermissions() };

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await getUsers();
      setUsers(data.users);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Redirect non-admins
  if (currentUser?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleOpen = (user = null) => {
    if (user) {
      setEditingUser(user);
      // Seed from saved permissions; fall back to full access, and fill any
      // newly-added section keys so the toggles all render.
      const base = fullPermissions();
      const saved = user.permissions || {};
      setForm({
        name: user.name, email: user.email, password: '', role: user.role,
        permissions: {
          view_price: saved.view_price !== false,
          sections: { ...base.sections, ...(saved.sections || {}) },
        },
      });
    } else {
      setEditingUser(null);
      setForm({ ...emptyForm, permissions: fullPermissions() });
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Admins always have full access — store no restrictions for them.
      const permissions = form.role === 'admin' ? null : form.permissions;
      if (editingUser) {
        const payload = { name: form.name, email: form.email, role: form.role, permissions };
        if (form.password) payload.password = form.password;
        await updateUser(editingUser.id, payload);
        setSnackbar({ open: true, message: 'User updated', severity: 'success' });
      } else {
        await createUser({ name: form.name, email: form.email, password: form.password, role: form.role, permissions });
        setSnackbar({ open: true, message: 'User created', severity: 'success' });
      }
      handleClose();
      fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Failed to save user';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser(deleteConfirm.id);
      setSnackbar({ open: true, message: 'User deleted', severity: 'success' });
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete user';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setDeleteConfirm(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Users
        </Typography>
        <Button variant="contained" startIcon={<AddRounded />} onClick={() => handleOpen()}>
          Add User
        </Button>
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.role === 'admin' ? 'Admin' : 'User'}
                        size="small"
                        color={u.role === 'admin' ? 'primary' : 'default'}
                        variant={u.role === 'admin' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpen(u)} sx={{ mr: 0.5 }}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={u.id === currentUser.id}
                        onClick={() => setDeleteConfirm(u)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={editingUser ? 'New Password (leave blank to keep)' : 'Password'}
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required={!editingUser}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            select
            label="Role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>

          {/* Per-user access — only for non-admins (admins have full access). */}
          {form.role !== 'admin' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Access</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.permissions.view_price !== false}
                    onChange={(e) => setForm((f) => ({ ...f, permissions: { ...f.permissions, view_price: e.target.checked } }))}
                  />
                }
                label={<Typography variant="body2">Can view product prices</Typography>}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                Sections this user can open:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {PERMISSION_SECTIONS.map((s) => (
                  <FormControlLabel
                    key={s.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={form.permissions.sections?.[s.key] !== false}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          permissions: { ...f.permissions, sections: { ...f.permissions.sections, [s.key]: e.target.checked } },
                        }))}
                      />
                    }
                    label={<Typography variant="body2">{s.label}</Typography>}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name || !form.email || (!editingUser && !form.password)}
          >
            {saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
